import {
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSportsbookSingleGameRelations,
  PAYOUT_AVAILABLE_SPORTS,
  PREMATCH_GAME_TYPE_FOR_SWARM
} from 'utils/constants/sportsbook/data-fields';
import { GAME_TYPE_LIVE } from 'utils/constants/sportsbook/generic';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { GLOBALS } from 'utils/generic/global-variables';
import { lazy } from 'utils/generic/lazy';
import { getMountPath, mountPoint } from 'utils/generic/route-manipulation';
import { showToastWarning } from 'utils/generic/show-toast-warning';
import { ESPORTS_ADDITIONAL_DATA } from 'utils/helpers/moba-games-statistics-options';
import { isMobile } from 'utils/is-mobile';
import { setMissingMarketGroupNameOther } from 'utils/sportsbook/set-missing-market-group-name-other';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import {
  ESportsbookLayoutTypes,
  ESportsbookPageTypes,
  TSportsbookConfigs
} from 'interfaces/sportsbook-configs';
import {
  GameInfoAdditionalDataMap,
  Sport
} from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { SingleGameViewSkeleton } from './SingleGameViewSkeleton';
import { addQuery, getSavedLocation } from 'components/QueryRoute';
import { Viewport } from 'components/Viewport';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { useSearchParams } from 'hooks/useSearchParams';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setAdditionalStatisticsData } from 'store/actions';
import { resetMatchStreamingState } from 'store/actions/match-streaming';
import {
  setAllMetaData,
  setCompetitionMetaData,
  setGameMetaData,
  setSportMetaData
} from 'store/actions/meta-data';
import {
  setSingleGameData,
  setSportsbookSelectedSport
} from 'store/actions/new-sportsbook';
import { TGameDataWithMoreInfo } from 'store/reducers/new-sportsbook';
import { getAdditionalStatisticsData } from 'store/selectors/e-sport-data';
import {
  getSingleGameData,
  getSportsbookSelectedSport
} from 'store/selectors/new-sportsbook';
import './index.less';

const { SingleGameViewMobile } = lazy(
  () => import(/* webpackChunkName: "single-view-mobile" */ './Mobile')
);

const { SingleGameViewDesktop } = lazy(
  () => import(/* webpackChunkName: "single-view-desktop" */ './Desktop')
);

export type TSingleGameViewProps = {
  gameData: TGameDataWithMoreInfo;
  onBack: () => void;
  isLive: boolean;
  openTimeLine: boolean;
  setOpenTimeLine: (arg: (prev: any) => boolean) => void;
  configs: TSportsbookConfigs;
  showGoBack?: boolean;
  showAdvancedHeader?: boolean;
};

export const SingleGameView = memo(
  ({
    showGoBack,
    showAdvancedHeader
  }: Pick<
    TSingleGameViewProps,
    'showGoBack' | 'showAdvancedHeader'
  >): ReactElement | null => {
    const configs = useSportsbookConfigs();
    const { params, path } = useSbRouteMatch();
    const { region, competition, game, sport } = params;
    const searchParams = useSearchParams();
    const { history, goBack } = useCustomHistory();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const additionalStatisticsData = useSelector(getAdditionalStatisticsData);
    const selectedSport = useSelector(getSportsbookSelectedSport);

    const currentMapData = useMemo(
      () => ESPORTS_ADDITIONAL_DATA[sport],
      [sport]
    );

    const [openTimeLine, setOpenTimeLine] = useState(false);

    const isFavoriteTeam =
      params.category === ENotRealSports.team ||
      params['0'] === ENotRealSports.team;

    const gameId = isFavoriteTeam && !isMobile() ? competition : game;

    const gameData = useSelector(getSingleGameData)?.[gameId];

    const isLive =
      gameData?.game.type === 1 || Number(searchParams.gameType) === 1;

    const hasAdditionalStatistics =
      Object.keys(ESPORTS_ADDITIONAL_DATA).includes(sport) && isLive;

    useScrollToTop(isMobile() ? 'singleGameViewPage' : 'desktopScrollArea', [
      gameData?.game.id
    ]);

    const setGameData = (swarmData: { sport: Record<string, Sport> }) => {
      const removeFinishedGameOnDesktop =
        swarmData?.sport &&
        Object.keys(swarmData.sport).length === 0 &&
        !isMobile();

      if (removeFinishedGameOnDesktop) {
        onBack();
        showToastWarning(t('account.gameIsNotAvailable'));
      }

      const data = setMissingMarketGroupNameOther(swarmData);

      dispatch(setSingleGameData({ id: gameId, data: data }));
    };

    useEffect(() => {
      if (!currentMapData) {
        return;
      }

      const maps = gameData?.game.info?.additional_data?.[currentMapData.maps];
      const selected =
        additionalStatisticsData.selected === -1
          ? gameData?.game.info?.additional_data?.[currentMapData.current] || -1
          : additionalStatisticsData.selected;

      const map: GameInfoAdditionalDataMap | undefined = (maps || []).find(
        map => Number(map[currentMapData.number]) === Number(selected)
      );

      if (maps?.length) {
        maps[maps.length - 1].timers =
          gameData?.game.info?.additional_data?.timers || [];
      }

      dispatch(setAdditionalStatisticsData({ maps, selected, map }));
    }, [gameData?.game.info?.additional_data, currentMapData]);

    useEffect(() => {
      dispatch(
        setAdditionalStatisticsData({
          selected: -1,
          map: undefined,
          maps: undefined
        })
      );
    }, [game]);

    const onBack = useCallback(() => {
      const isAdvancedLayout =
        configs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED;

      if (isAdvancedLayout) {
        goBack(`${mountPoint(window.getPathname())}`);

        return;
      }

      if (GLOBALS.sportsBook.redirectedFromFavorite) {
        GLOBALS.sportsBook.redirectedFromFavorite = false;
        const currentPathName = window.location.pathname;
        goBack(`${mountPoint(window.getPathname())}`);

        if (window.location.pathname === currentPathName) {
          goBack(`${mountPoint(window.getPathname())}`);
        }

        dispatch(setSportsbookSelectedSport(null));

        return;
      }

      const pathname = window.getPathname();
      const lastIndex = pathname.lastIndexOf('/');
      const pathWithoutGame = pathname.slice(0, lastIndex);
      const savedUrl = getSavedLocation(
        'mobileSportsbookGames',
        configs.spbLayoutType !== ESportsbookLayoutTypes.AMERICAN
      );

      const backUrl = savedUrl.includes('/team') ? savedUrl : null;

      const usedDesktopUrlStructureOnMobile =
        params.category &&
        [ENotRealSports.todayEvents, ENotRealSports.boostedOdds].includes(
          params.category as ENotRealSports
        ) &&
        configs.spbLayoutType !== ESportsbookLayoutTypes.AMERICAN &&
        params.competition &&
        params.game &&
        isMobile();

      const isUsaFavTeamWithNoStoredUrl =
        isFavoriteTeam &&
        !savedUrl &&
        configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN;

      let toSportAliasBackUrl =
        ((configs.spbLayoutType === ESportsbookLayoutTypes.EUROPEAN ||
          isMobile()) &&
          isLive) ||
        isUsaFavTeamWithNoStoredUrl
          ? `${getMountPath(path)}/${sport}`
          : null;

      if (usedDesktopUrlStructureOnMobile) {
        toSportAliasBackUrl = `${getMountPath(path)}/${params.category}${
          params.category === ENotRealSports.todayEvents ? `/${sport}` : ''
        }`;
      }

      if (
        (isMobile() ||
          configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN) &&
        isLive
      ) {
        dispatch(resetMatchStreamingState(true));
      }

      history.push({
        pathname: toSportAliasBackUrl || backUrl || pathWithoutGame,
        search: window.location.search
      });
    }, [isLive, history, sport, searchParams]);

    useEffect(() => {
      if (!SpringConfigs.MOCKED_DATA && !gameId) {
        return;
      }

      const rid = RidGenerator.gForSubscribe('SINGLE_GAME_VIEW');

      let where: Record<string, any> = {
        sport: {
          alias: sport
        },
        game: {
          id: parseInt(String(gameId))
        }
      };

      if (configs.spbLayoutType !== ESportsbookLayoutTypes.ADVANCED) {
        where.game = {
          ...where.game,
          ...(configs.pageType === ESportsbookPageTypes.live
            ? { type: GAME_TYPE_LIVE }
            : PREMATCH_GAME_TYPE_FOR_SWARM)
        };
      }

      if (region && !isFavoriteTeam) {
        where.region = {
          alias: region
        };
      }

      if (competition && (!isFavoriteTeam || isMobile())) {
        where.competition = {
          id: parseInt(String(competition))
        };
      }

      if (SpringConfigs.MOCKED_DATA) {
        where = {
          game: {
            id: 19095741
          },
          sport: {
            alias: 'Soccer'
          },
          region: {
            alias: 'Europe'
          },
          competition: {
            id: 566
          }
        };
      }

      const shouldIncludePrematchExpressId =
        configs.pageType === ESportsbookPageTypes.prematch;

      const isPayoutAvailable = PAYOUT_AVAILABLE_SPORTS.includes(sport);

      executeCommand(
        rid,
        getSportsbookSingleGameRelations(
          { region: ['name', 'alias'] },
          shouldIncludePrematchExpressId,
          isPayoutAvailable
        ),
        where,
        setGameData,
        true,
        setGameData
      );

      return () => {
        unsubscribeCommand(rid);
      };
    }, [sport, region, competition, gameId]);

    useEffect(() => {
      if (isMobile()) {
        document.body.style.overflow = 'hidden';
      }

      return () => {
        if (isMobile()) {
          document.body.style.overflow = 'unset';
        }

        getSavedLocation('mobileSportsbookGames');

        if (configs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED) {
          history.push(addQuery({ gameType: undefined }));
        }
      };
    }, []);

    useEffect(() => {
      if (gameData) {
        dispatch(
          setAllMetaData({
            sport: { name: gameData.sportName },
            competition: { name: gameData.competitionName },
            game: {
              team1_name: gameData.game.team1_name,
              team2_name: gameData.game.team2_name,
              start_ts: gameData.game.start_ts
            }
          })
        );
      }
    }, [gameData]);

    useLayoutEffect(() => {
      if (
        configs.spbLayoutType === ESportsbookLayoutTypes.ADVANCED &&
        isLive &&
        Number(searchParams.gameType) !== GAME_TYPE_LIVE
      ) {
        history.push(addQuery({ gameType: GAME_TYPE_LIVE.toString() }));
      }
    }, [isLive, game, searchParams.gameType]);

    useEffect(() => {
      return () => {
        if (isMobile()) {
          if (isLive) {
            dispatch(setCompetitionMetaData(undefined));
            dispatch(setGameMetaData(undefined));
          } else {
            dispatch(setGameMetaData(undefined));
          }
        } else {
          if (configs.spbLayoutType === ESportsbookLayoutTypes.EUROPEAN) {
            dispatch(setCompetitionMetaData(undefined));
            dispatch(setGameMetaData(undefined));
          } else if (
            configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN
          ) {
            dispatch(setGameMetaData(undefined));

            if (!isLive) {
              dispatch(setCompetitionMetaData(undefined));
              dispatch(setSportMetaData({ name: selectedSport?.name || '' }));
            }
          }
        }
      };
    }, []);

    return (
      <Viewport
        fallback={
          <SingleGameViewSkeleton
            isLive={isLive}
            onBack={onBack}
            showGoBack={showGoBack}
            hasAdditionalStatistics={hasAdditionalStatistics}
            showAdvancedHeader={showAdvancedHeader}
          />
        }
        mobile={SingleGameViewMobile}
        desktop={SingleGameViewDesktop}
        innerProps={{
          gameData,
          onBack,
          isLive,
          openTimeLine,
          setOpenTimeLine,
          configs,
          showGoBack,
          showAdvancedHeader
        }}
      />
    );
  }
);
